import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Card, CardBody, CardHeader, Button } from "reactstrap";
import ImageCropModal from "./ImageCropModal";
// import ImageCropModal from './ImageCropModal';

const DropzoneComponent = ({
  selectedFiles,
  setSelectedFiles,
  existingImage = null,
  customStyles = {},
  fileType = "image", // "image" or "video"
}) => {
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();

      reader.onload = () => {
        if (fileType === "image") {
          setImageToCrop(reader.result);
          setCropModalOpen(true);
        } else {
          // For video, store directly without cropping
          setSelectedFiles([
            {
              isExisting: false,
              preview: reader.result,
              payload: file,
            },
          ]);
        }
      };

      reader.readAsDataURL(file);
    }
  }, [fileType, setSelectedFiles]);

  const handleCropComplete = (croppedImage) => {
    setSelectedFiles([
      {
        isExisting: false,
        preview: croppedImage.url,
        payload: croppedImage.file,
      },
    ]);
    setImageToCrop(null);
  };

  const acceptConfig = fileType === "video" 
    ? { "video/*": [".mp4"] }
    : { "image/*": [".png", ".jpg", ".jpeg"] };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptConfig,
    maxFiles: 1,
    multiple: false,
  });

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setSelectedFiles([]);
  };

  const isImage = fileType === "image";
  const fileIcon = isImage ? "ri-image-add-line" : "ri-video-add-line";
  const dragActiveText = isImage ? "Drop image here" : "Drop video here";
  const dragInactiveText = isImage 
    ? "Drop image here or click to browse" 
    : "Drop video here or click to browse";
  const supportedTypes = isImage 
    ? "Supports: PNG, JPG, JPEG"
    : "Supports: MP4 (Max 30MB)";

  return (
    <>
      <div>
        {selectedFiles.length === 0 ? (
          <div
            {...getRootProps()}
            style={{
              border: customStyles.dropzone?.border || "2px dashed #dee2e6",
              borderRadius: customStyles.dropzone?.borderRadius || "8px",
              padding: customStyles.dropzone?.padding || "40px 20px",
              textAlign: customStyles.dropzone?.textAlign || "center",
              backgroundColor: isDragActive
                ? "#e7f3ff"
                : customStyles.dropzone?.backgroundColor || "#f8f9fa",
              cursor: customStyles.dropzone?.cursor || "pointer",
              transition: "all 0.3s ease",
            }}
          >
            <input {...getInputProps()} />
            <i
              className={fileIcon}
              style={
                customStyles.icon || {
                  fontSize: "48px",
                  color: "#adb5bd",
                  marginBottom: "12px",
                  display: "block",
                }
              }
            ></i>
            <h6
              style={
                customStyles.title || {
                  margin: "0 0 8px 0",
                  color: "#495057",
                  fontSize: "14px",
                  fontWeight: 500,
                }
              }
            >
              {isDragActive ? dragActiveText : dragInactiveText}
            </h6>
            <p
              style={
                customStyles.subtitle || {
                  margin: 0,
                  color: "#6c757d",
                  fontSize: "12px",
                }
              }
            >
              {supportedTypes}
            </p>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            <div
              style={{
                width: "100%",
                height: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                // backgroundColor: "#f8f9fa",
                // borderRadius: "8px",
                overflow: "hidden",
                // border: "1px solid #dee2e6",
              }}
            >
              {isImage ? (
                <img
                  src={selectedFiles[0].preview}
                  alt="Product"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <video
                  src={selectedFiles[0].preview}
                  controls
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              )}
            </div>
            <Button
              color="danger"
              size="sm"
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                padding: "0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={handleRemoveFile}
            >
              <i className="ri-close-line" style={{ fontSize: "16px" }}></i>
            </Button>
            <div style={{ marginTop: "12px", textAlign: "center" }}>
              <Button
                color="primary"
                size="sm"
                onClick={() => {
                  const input = document.createElement("input");
                  input.type = "file";
                  input.accept = isImage ? "image/*" : "video/*";
                  input.onchange = (e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = () => {
                        if (isImage) {
                          setImageToCrop(reader.result);
                          setCropModalOpen(true);
                        } else {
                          setSelectedFiles([
                            {
                              isExisting: false,
                              preview: reader.result,
                              payload: file,
                            },
                          ]);
                        }
                      };
                      reader.readAsDataURL(file);
                    }
                  };
                  input.click();
                }}
              >
                <i className={isImage ? "ri-crop-line" : "ri-upload-cloud-line"} style={{ marginRight: "6px" }}></i>
                {isImage ? "Change Image" : "Change Video"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {isImage && (
        <ImageCropModal
          isOpen={cropModalOpen}
          toggle={() => {
            setCropModalOpen(false);
            setImageToCrop(null);
          }}
          imageSrc={imageToCrop}
          onCropComplete={handleCropComplete}
          aspectRatio={1}
        />
      )}
    </>
  );
};

export default DropzoneComponent;
